import SubmitButton from "Components/Buttons/SubmitButton";
import { useFormik } from "formik";
import { useHandleBack } from "helpers/functions/useHandleBack";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { addGst, updateGst } from "slices/gst/thunk";
import * as Yup from "yup";

const standardRates = [0, 5, 12, 18, 28];

const GSTForm: React.FC = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const handleBack = useHandleBack("/gst");

  const isEdit = Boolean(id);

  const gstList = useSelector((state: any) => state.gst?.list || []);

  const [initialData, setInitialData] = useState({
    GSTName: "",
  });

  useEffect(() => {
    if (isEdit && state?.gstData) {
      setInitialData({
        GSTName: state.gstData.gst_percentage?.toString() || "",
      });
    }
  }, [id, isEdit, state]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      GSTName: initialData.GSTName,
    },
    validationSchema: Yup.object({
      GSTName: Yup.string()
        .required("Please enter GST %")
        .trim()
        .matches(/^\d*\.?\d*$/, "Only numeric values are allowed")
        .test("valid-number", "Please enter a valid number", (value) => {
          if (!value) return false;
          const num = parseFloat(value);
          return !isNaN(num) && isFinite(num);
        })

        .test("unique-gst", "GST percentage already exists", function (value) {
          if (!value) return true;

          const inputNumber = Number(parseFloat(value).toFixed(2)); // normalize input

          // Make sure gstList is loaded
          if (!gstList || gstList.length === 0) return true;

          const duplicate = gstList.some((gst: any) => {
            if (!gst || gst.gst_percentage == null) return false;

            const gstValue = Number(parseFloat(gst.gst_percentage).toFixed(2));
            const gstId = gst.gst_id || gst.id || gst.ID;

            if (isEdit && gstId?.toString() === id?.toString()) return false;

            return gstValue === inputNumber;
          });

          return !duplicate;
        }),
    }),

    onSubmit: async (values) => {
      const gstPercentage = parseFloat(values.GSTName);
      const payload = { gst_percentage: parseFloat(gstPercentage.toFixed(2)) };

      try {
        if (isEdit) {
          await dispatch(updateGst({ id: id!, ...payload })).unwrap();
          toast.success("GST updated successfully");
        } else {
          await dispatch(addGst(payload)).unwrap();
          toast.success("GST added successfully");
        }
        handleBack();
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong.";
        if (
          errorMessage.toLowerCase().includes("duplicate") ||
          errorMessage.toLowerCase().includes("already exists")
        ) {
          toast.error("GST percentage already exists");
        } else {
          toast.error(errorMessage);
        }
      }
    },
  });

  return (
    <div className="page-content px-4">
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          padding: "30px",
          boxShadow: "0 0 15px rgba(0,0,0,0.05)",
        }}
      >
        <Form autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{ fontSize: "24px", cursor: "pointer" }}
                  onClick={handleBack}
                >
                  ←
                </span>
                <h5 className="mb-0 fw-bold">
                  {isEdit ? "Edit GST" : "Add GST"}
                </h5>
              </div>

              <div className="text-muted">
                <Link
                  to="/gst"
                 className="text-muted" style={{ fontSize: "0.9rem" }}
                >
                  GST
                </Link>{" "}
                &gt;{" "}
                <span className="text-muted" style={{ fontSize: "0.9rem" }}>
                  {isEdit ? "Edit GST" : "Add GST"}
                </span>
              </div>
            </div>

            <Col lg={12}>
              <div className="mb-3">
                <Form.Label>
                  GST Percentage <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="GSTName"
                  placeholder="Enter GST Percentage"
                  value={formik.values.GSTName}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*\.?\d*$/.test(value) || value === "") {
                      formik.setFieldValue("GSTName", value);
                    }
                  }}
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    if (value && !isNaN(parseFloat(value))) {
                      formik.setFieldValue(
                        "GSTName",
                        parseFloat(value).toString()
                      );
                    }
                    formik.handleBlur(e);
                  }}
                  isInvalid={!!formik.errors.GSTName && formik.touched.GSTName}
                  style={{
                    height: "40px",
                    lineHeight: "1.5",
                    fontSize: "0.875rem",
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.GSTName}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                </Form.Text>
              </div>
            </Col>
          </Row>

          <div className="d-flex justify-content-center gap-3 py-3">
            <Button
              variant="outline-secondary"
              style={{
                color: "#878899",
                borderColor: "#B0BAC9",
                backgroundColor: "transparent",
              }}
              onClick={handleBack}
            >
              Cancel
            </Button>

            {/* <Button
              type="submit"
              className="btn"
              style={{
                backgroundColor: "#0073BB",
                borderColor: "#0073BB",
                color: "#fff",
              }}
            >
              {isEdit ? "Update" : "Add"}
            </Button> */}
            {/* Reusable submit button with spinner */}
            <SubmitButton isSubmitting={formik.isSubmitting} isEdit={isEdit} />
          </div>
        </Form>
      </div>
    </div>
  );
};

export default GSTForm;
